/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { forwardRef } from 'react';
import * as React from 'react';

import type { CareerRole_PathwayLevel as PathwayLevel } from '__generated__/graphql-types';

import Imgix from 'js/components/Imgix';
import { FormattedMessage, FormattedNumber } from 'js/lib/coursera.react-intl';
import type { TrackingData } from 'js/lib/retracked';

import { Hidden, ProductCard, Typography2, VisuallyHidden, breakpoints, typography2 } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import CareerCardCredentials from 'bundles/unified-common/components/CareerCardCredentials';
import type { CareerRoleType } from 'bundles/unified-common/types/careerRoles';

import _t from 'i18n!nls/unified-common';

const styles = {
  container: css`
    height: 100%;
    cursor: pointer;

    .cds-CommonCard-title {
      ${typography2.subtitleLarge};
      margin-bottom: var(--cds-spacing-50);

      ${breakpoints.down('xs')} {
        ${typography2.subtitleMedium};
      }
    }

    .cds-ProductCard-listPreviewImage {
      align-self: center;
    }

    .cds-ProductCard-list {
      border: 2px solid var(--cds-color-neutral-stroke-primary-weak);
      min-width: unset;

      ${breakpoints.down('xs')} {
        border-width: 1px;
      }
    }

    .cds-ProductCard-content {
      gap: 0 !important;
    }
  `,
  bodyContainer: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-150);

    strong {
      color: var(--cds-color-neutral-primary);
    }

    h3 {
      ${typography2.bodySecondary};
      font-weight: bold;
    }
  `,
};

type Props = CareerRoleType & {
  proficiencyLevel?: PathwayLevel;
  hideCredentials?: boolean;
  showPathwaysLoadingState?: boolean;
  eventingData?: TrackingData;
};

/**
 * @description CareerRoleCard is a component that displays a card that represents a career role.
 */
const CareerRoleCard = forwardRef<HTMLDivElement, Props>(
  (
    {
      name: roleName,
      shortDescription,
      roleCardImageUrl,
      jobPostingData,
      merchandisingSkillNames,
      pathways,
      slug,
      hideCredentials = false,
      eventingData,
      proficiencyLevel,
      showPathwaysLoadingState,
    },
    ref
  ) => {
    const track = useTracker();

    const { medianSalary, numJobsAvailable, currencyCode } = jobPostingData ?? {};

    const careerCardEventingV3Data = {
      ...eventingData,
      role: {
        roleSlug: slug,
      },
    };

    const visibilityRef = useVisibilityTracker('view_role_card', careerCardEventingV3Data);
    const combinedRef = (node: HTMLDivElement | null) => {
      (visibilityRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      if (ref) {
        if (typeof ref === 'function') {
          ref(node);
        } else {
          ref.current = node;
        }
      }
    };

    if (!slug) {
      return null;
    }

    const onSelectCard = () => {
      track('click_role_card', careerCardEventingV3Data);
    };

    const recommenderId = eventingData?.carousel?.id;

    const credentials =
      pathways?.entities?.map((pathway) => ({
        id: pathway.id,
        slug: pathway.slug,
        name: pathway.name,
        partners: pathway.partners,
        urlPath: `${pathway.urlPath}${recommenderId ? `?recommenderId=${recommenderId}` : ''}`,
      })) ?? [];

    const createHref = (roleSlug: string) => {
      const searchParams = new URLSearchParams();

      if (proficiencyLevel) {
        searchParams.set('level', proficiencyLevel.toLowerCase());
      }

      if (recommenderId) {
        searchParams.set('recommenderId', recommenderId);
      }

      const queryString = searchParams.toString();
      return `/career-academy/roles/${roleSlug}${queryString ? `?${queryString}` : ''}`;
    };

    const credentialsEventingData = {
      ...(eventingData?.carousel ? { carousel: { id: recommenderId } } : {}),
      ...(eventingData?.productCard ? { productCard: eventingData.productCard } : {}),
    };

    const commonProductCardProps = {
      ref: combinedRef,
      productType: 'role' as const,
      renderPreviewImage: () => (roleCardImageUrl ? <Imgix src={roleCardImageUrl} alt="" maxWidth={430} /> : null),
      title: {
        name: roleName ?? '',
        linkProps: {
          href: createHref(slug),
          tabIndex: -1, // title should not be focusable
        },
      },
      onKeyPress: (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
          onSelectCard();
        }
      },
      onClick: onSelectCard,
      tabIndex: 0,
    };

    return (
      <React.Fragment>
        <Hidden smUp css={styles.container}>
          <ProductCard
            {...commonProductCardProps}
            variant="list"
            body={
              <div css={styles.bodyContainer}>
                <Typography2 component="p" variant="bodySecondary" color="supportText">
                  {shortDescription}
                </Typography2>
              </div>
            }
          />
        </Hidden>
        <Hidden only="xs" css={styles.container}>
          <ProductCard
            {...commonProductCardProps}
            variant="grid"
            body={
              <div css={styles.bodyContainer}>
                <VisuallyHidden component="span">{_t('description:')}</VisuallyHidden>
                <Typography2 component="p" variant="bodySecondary" color="supportText">
                  {shortDescription}
                </Typography2>
                {merchandisingSkillNames && merchandisingSkillNames.length > 0 && (
                  <Typography2 component="p" variant="bodySecondary" color="supportText" data-testid="role-skills">
                    <strong>{_t('If you like: ')}</strong>
                    {merchandisingSkillNames.slice(0, 3).join(', ')}
                    <VisuallyHidden component="span">.</VisuallyHidden>
                  </Typography2>
                )}
                <div>
                  {!!medianSalary && currencyCode && (
                    <Typography2 data-testid="median-salary" variant="bodySecondary" color="supportText" component="p">
                      <VisuallyHidden component="span">{_t('This role has a ')}</VisuallyHidden>
                      <FormattedMessage
                        message={_t('{medianSalary} median salary ¹')}
                        medianSalary={
                          <strong>
                            <FormattedNumber
                              value={medianSalary}
                              // eslint-disable-next-line react/style-prop-object
                              style="currency"
                              currency={currencyCode}
                              maximumFractionDigits={0}
                              minimumFractionDigits={0}
                            />
                          </strong>
                        }
                      />
                      <VisuallyHidden component="span">.</VisuallyHidden>
                    </Typography2>
                  )}
                  {!!numJobsAvailable && (
                    <Typography2 data-testid="jobs-available" variant="bodySecondary" color="supportText" component="p">
                      <VisuallyHidden component="span">{_t('This role has approximately ')}</VisuallyHidden>
                      <FormattedMessage
                        message={_t('{jobsAvailable} jobs available ¹')}
                        jobsAvailable={
                          <strong>
                            <FormattedNumber value={numJobsAvailable} />
                          </strong>
                        }
                      />
                      <VisuallyHidden component="span">.</VisuallyHidden>
                    </Typography2>
                  )}
                </div>
                {!hideCredentials && (
                  <CareerCardCredentials
                    showPathwaysLoadingState={showPathwaysLoadingState}
                    credentials={credentials}
                    roleName={roleName}
                    roleSlug={slug}
                    proficiencyLevel={proficiencyLevel}
                    linkVariant="bodySecondary"
                    eventingData={credentialsEventingData}
                  />
                )}
              </div>
            }
          />
        </Hidden>
      </React.Fragment>
    );
  }
);

export default CareerRoleCard;
